import { useEffect, useState, useMemo } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { routesApi, salesApi, reportsApi, customersApi, settingsApi } from '../../services/api'
import {
  LineChart, Line, BarChart, Bar, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts'
import { Route, ShoppingCart, DollarSign, Clock, AlertTriangle } from 'lucide-react'
import DistrictBubblesMap from '../../components/DistrictBubblesMap'
import KpiCard from '../../components/KpiCard'
import SellerInsightCards from '../../components/SellerInsightCards'
import { useSortableData } from '../../hooks/useSortableData'
import SortableTh from '../../components/SortableTh'

const COLORS = ['#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ef4444']

interface CollectionsByCustomer {
  id: number
  name: string
  pending_amount: number
  overdue_amount: number
  pending_count: number
}

interface InactiveCustomer {
  id: number
  name: string
  district: string | null
  last_sale_date: string | null
  days_inactive: number | null
}

export default function DashboardPage() {
  const { user } = useAuth()
  const [routes, setRoutes] = useState<any[]>([])
  const [sales, setSales] = useState<any[]>([])
  const [collections, setCollections] = useState<{
    summary: { total_pending: number; total_overdue: number; customers_with_debt: number }
    by_customer: CollectionsByCustomer[]
  } | null>(null)
  const [inactiveCustomers, setInactiveCustomers] = useState<InactiveCustomer[]>([])
  const [inactiveDaysThreshold, setInactiveDaysThreshold] = useState(30)
  const [prefs, setPrefs] = useState<Record<string, boolean>>({})
  const [loading, setLoading] = useState(true)
  const [showInsights, setShowInsights] = useState(false)

  const isVisible = (key: string) => prefs[key] !== false

  // Insight cards only appear right after an actual login (see useAuth.tsx's
  // login()), never on a page refresh/session-restore in the same tab --
  // read the flag once and clear it immediately so a refresh doesn't
  // re-trigger them.
  useEffect(() => {
    if (sessionStorage.getItem('just_logged_in')) {
      setShowInsights(true)
      sessionStorage.removeItem('just_logged_in')
    }
  }, [])

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [routesRes, salesRes, collectionsRes, inactiveRes, prefsRes] = await Promise.all([
          routesApi.list(),
          salesApi.list(),
          reportsApi.getCollections(),
          customersApi.inactive(),
          settingsApi.getDashboardPrefs(),
        ])
        setRoutes(routesRes.data.routes || [])
        setSales(salesRes.data.sales || [])
        setCollections(collectionsRes.data)
        setInactiveCustomers(inactiveRes.data.customers || [])
        setPrefs(prefsRes.data.dashboard_prefs || {})
      } catch (error) {
        console.error('Error fetching data:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  const collectionsChartData = useMemo(() => {
    return (collections?.by_customer || [])
      .slice(0, 10)
      .map((c) => ({
        name: c.name,
        vencido: c.overdue_amount,
        por_vencer: c.pending_amount - c.overdue_amount,
      }))
  }, [collections])

  const neverPurchasedCount = useMemo(
    () => inactiveCustomers.filter((c) => c.days_inactive === null).length,
    [inactiveCustomers]
  )

  const inactiveChartData = useMemo(() => {
    return inactiveCustomers
      .filter((c) => c.days_inactive !== null && c.days_inactive >= inactiveDaysThreshold)
      .sort((a, b) => (b.days_inactive || 0) - (a.days_inactive || 0))
      .slice(0, 10)
      .map((c) => ({ name: c.name, days: c.days_inactive }))
  }, [inactiveCustomers, inactiveDaysThreshold])

  const totalRevenue = useMemo(
    () => sales.reduce((sum, s) => sum + Number(s.total_amount || 0), 0),
    [sales]
  )

  const salesByDate = useMemo(() => {
    const map = new Map<string, { revenue: number; count: number }>()
    sales.forEach((s) => {
      const date = new Date(s.sale_date).toLocaleDateString()
      const existing = map.get(date) || { revenue: 0, count: 0 }
      map.set(date, {
        revenue: existing.revenue + Number(s.total_amount || 0),
        count: existing.count + 1,
      })
    })
    return Array.from(map.entries())
      .map(([date, data]) => ({ date, ...data }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
  }, [sales])

  const salesByVariant = useMemo(() => {
    const map = new Map<string, { revenue: number; quantity: number }>()
    sales.forEach((s) => {
      const name = s.variant_name || 'Desconocido'
      const existing = map.get(name) || { revenue: 0, quantity: 0 }
      map.set(name, {
        revenue: existing.revenue + Number(s.total_amount || 0),
        quantity: existing.quantity + (s.quantity || 0),
      })
    })
    return Array.from(map.entries()).map(([name, data]) => ({ name, ...data }))
  }, [sales])

  const topClients = useMemo(() => {
    const map = new Map<string, { name: string; total: number; count: number }>()
    sales.forEach((s) => {
      const name = s.customer_name || 'Desconocido'
      const existing = map.get(name) || { name, total: 0, count: 0 }
      existing.total += Number(s.total_amount || 0)
      existing.count += 1
      map.set(name, existing)
    })
    return Array.from(map.values())
      .sort((a, b) => b.total - a.total)
      .slice(0, 5)
  }, [sales])

  const salesByDistrict = useMemo(() => {
    const map = new Map<string, { district: string; count: number; revenue: number }>()
    sales.forEach((s) => {
      const district = s.customer_district || 'Desconocido'
      const existing = map.get(district) || { district, count: 0, revenue: 0 }
      existing.count += 1
      existing.revenue += Number(s.total_amount || 0)
      map.set(district, existing)
    })
    return Array.from(map.values()).sort((a, b) => b.count - a.count)
  }, [sales])

  // Sort the full sales list (not just the current top-10 slice) so that,
  // e.g., sorting by amount actually surfaces the top-10-by-amount rather
  // than just re-shuffling whichever 10 rows happened to be most recent.
  const { sortedData: sortedSales, sortConfig: salesSortConfig, requestSort: requestSalesSort } = useSortableData(sales)

  if (loading) {
    return <div className="text-center py-8">Cargando...</div>
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-espresso-800 mb-6">
        Bienvenido, {user?.name}
      </h1>

      {showInsights && (
        <SellerInsightCards
          sales={sales}
          collections={collections}
          inactiveCustomers={inactiveCustomers}
          neverPurchasedCount={neverPurchasedCount}
        />
      )}

      {/* Summary Cards */}
      <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-espresso-500">
        Resumen
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <KpiCard
          icon={Route}
          label="Rutas Pendientes"
          value={routes.filter(r => r.status === 'pending').length}
          accent="terracotta"
        />
        <KpiCard
          icon={ShoppingCart}
          label="Ventas Totales"
          value={sales.length}
          accent="green"
        />
        <KpiCard
          icon={DollarSign}
          label="Ingresos Totales"
          value={`$${totalRevenue.toFixed(2)}`}
          accent="blue"
        />
      </div>

      {/* Collections Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <KpiCard
          icon={Clock}
          label="Cobranza Pendiente"
          value={`$${(collections?.summary.total_pending || 0).toFixed(2)}`}
          accent="terracotta"
          subtext={`${collections?.summary.customers_with_debt || 0} cliente(s) con saldo pendiente`}
        />
        <KpiCard
          icon={AlertTriangle}
          label="Cobranza Vencida"
          value={`$${(collections?.summary.total_overdue || 0).toFixed(2)}`}
          accent="red"
          subtext="Pasó la fecha de pago esperada"
        />
      </div>

      {/* Row 1: Sales Trend + Sales by Product */}
      <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-espresso-500">
        Tendencias
      </h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {isVisible('seller.sales_trend') && (
        <div className="bg-cream-50 shadow rounded-lg border border-cream-200 p-6">
          <h3 className="text-lg font-medium text-espresso-800 mb-4">Tendencia de Ventas</h3>
          {salesByDate.length === 0 ? (
            <p className="text-espresso-500">No hay datos de ventas aún.</p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={salesByDate}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip formatter={(value: number) => [`$${value.toFixed(2)}`, 'Ingresos']} />
                <Legend />
                <Line type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2} name="Ingresos ($)" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
        )}

        {isVisible('seller.sales_by_product') && (
        <div className="bg-cream-50 shadow rounded-lg border border-cream-200 p-6">
          <h3 className="text-lg font-medium text-espresso-800 mb-4">Ventas por Producto</h3>
          {salesByVariant.length === 0 ? (
            <p className="text-espresso-500">No hay datos de ventas aún.</p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={salesByVariant}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip formatter={(value: number) => [`$${value.toFixed(2)}`, 'Ingresos']} />
                <Legend />
                <Bar dataKey="revenue" name="Ingresos ($)" radius={[4, 4, 0, 0]}>
                  {salesByVariant.map((_: any, i: number) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        )}
      </div>

      {/* Row 2: Top 5 Clients + District Bubbles */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {isVisible('seller.top_clients') && (
        <div className="bg-cream-50 shadow rounded-lg border border-cream-200 p-6">
          <h3 className="text-lg font-medium text-espresso-800 mb-4">Top 5 Clientes</h3>
          {topClients.length === 0 ? (
            <p className="text-espresso-500">No hay datos de ventas aún.</p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={topClients}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip formatter={(value: number) => [`$${value.toFixed(2)}`, 'Total']} />
                <Legend />
                <Bar dataKey="total" name="Ingresos Totales" radius={[4, 4, 0, 0]}>
                  {topClients.map((_: any, i: number) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        )}

        {isVisible('seller.sales_by_district') && (
        <div className="bg-cream-50 shadow rounded-lg border border-cream-200 p-6">
          <h3 className="text-lg font-medium text-espresso-800 mb-4">Ventas por Distrito</h3>
          {salesByDistrict.length === 0 ? (
            <p className="text-espresso-500">No hay datos de ventas aún.</p>
          ) : (
            <DistrictBubblesMap districts={salesByDistrict} />
          )}
        </div>
        )}
      </div>

      {/* Row 3: Collections + Inactive Customers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {isVisible('seller.collections') && (
        <div className="bg-cream-50 shadow rounded-lg border border-cream-200 p-6">
          <h3 className="text-lg font-medium text-espresso-800 mb-4">Cartera de Cobranza por Cliente</h3>
          {collectionsChartData.length === 0 ? (
            <p className="text-espresso-500">No hay saldos pendientes de cobro.</p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={collectionsChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip formatter={(value: number) => [`$${value.toFixed(2)}`, '']} />
                <Legend />
                <Bar dataKey="vencido" stackId="a" name="Vencido" fill="#ef4444" radius={[0, 0, 0, 0]} />
                <Bar dataKey="por_vencer" stackId="a" name="Por Vencer" fill="#f59e0b" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        )}

        {isVisible('seller.inactive_customers') && (
        <div className="bg-cream-50 shadow rounded-lg border border-cream-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-espresso-800">Clientes Inactivos</h3>
            <div className="flex items-center gap-2 text-sm text-espresso-600">
              <label htmlFor="inactive-days">Sin compra hace más de</label>
              <input
                id="inactive-days"
                type="number"
                min={1}
                max={365}
                value={inactiveDaysThreshold}
                onChange={(e) => setInactiveDaysThreshold(Math.max(1, Number(e.target.value) || 1))}
                className="w-16 border border-espresso-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
              />
              <span>días</span>
            </div>
          </div>
          {neverPurchasedCount > 0 && (
            <p className="text-xs text-terracotta-700 mb-2">
              {neverPurchasedCount} cliente(s) registrados que nunca han comprado (no incluidos en el gráfico).
            </p>
          )}
          {inactiveChartData.length === 0 ? (
            <p className="text-espresso-500">No hay clientes inactivos con ese umbral.</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={inactiveChartData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={100} />
                <Tooltip formatter={(value: number) => [`${value} días`, 'Inactivo']} />
                <Bar dataKey="days" name="Días sin comprar" fill="#a8482a" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        )}
      </div>

      {/* Recent Sales Table */}
      {isVisible('seller.recent_sales') && (
      <>
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-espresso-500">
          Listados
        </h2>
        <div className="bg-cream-50 shadow rounded-lg border border-cream-200 p-6">
        <h3 className="text-lg font-medium text-espresso-800 mb-4">Ventas Recientes</h3>
        {sales.length === 0 ? (
          <p className="text-espresso-500">No hay ventas registradas aún.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-espresso-100">
              <thead>
                <tr>
                  <SortableTh label="Fecha" sortKey="sale_date" sortConfig={salesSortConfig} onSort={requestSalesSort} />
                  <SortableTh label="Variante" sortKey="variant_name" sortConfig={salesSortConfig} onSort={requestSalesSort} />
                  <SortableTh label="Cliente" sortKey="customer_name" sortConfig={salesSortConfig} onSort={requestSalesSort} />
                  <SortableTh label="Cantidad" sortKey="quantity" sortConfig={salesSortConfig} onSort={requestSalesSort} />
                  <SortableTh label="Total" sortKey="total_amount" sortConfig={salesSortConfig} onSort={requestSalesSort} />
                </tr>
              </thead>
              <tbody className="bg-cream-50 divide-y divide-espresso-100">
                {sortedSales.slice(0, 10).map((sale) => (
                  <tr key={sale.id}>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-800">
                      {new Date(sale.sale_date).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-800">
                      {sale.variant_name}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-800">
                      {sale.customer_name || '-'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-800">
                      {sale.quantity}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-800">
                      ${Number(sale.total_amount || 0).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </div>
      </>
      )}
    </div>
  )
}
